// This is a replacement for parts of `src/rxdb/RxdbManager.ts`
import { LiveInterval } from '../constants';
import { Crumbs } from './bakery/Crumbs';
import { createFamilies } from './createFamilies';
import { Database } from './Database';
import { crumbleFamily, Family } from './Family/Family';
import { synchronize } from './Family/synchronize';
import { logError } from './logError';
import { ReplicationState } from './replicate';

export interface Replication {
  readonly families: Family[];
  readonly reSyncTimeouts: NodeJS.Timeout[];
  readonly replicationStates: ReplicationState<unknown>[];
}

export function createReplication(database: Database, idToken: string): Replication {
  const families = createFamilies(database, idToken);
  const reSyncTimeouts: NodeJS.Timeout[] = [];

  const replicationStates = families.reduce((state, family) => {
    Object.values(family.members).forEach(member => {
      state.add(member.replicationState);
    });

    // Note that this is an async function.
    // We're not awaiting it here; we're just kicking off the first synchronization
    // and letting it complete on its own.
    // As long as the family is synchronizing,
    // the underlying promise can be accessed through `family.syncState.promise`.
    synchronize(family).catch(error => {
      logError('Error on initial family synchronization', error, crumbleFamily(family));
    });

    // 60 seconds was the interval for signatures and inspection results when all of these were independent.
    // The deviation from the default 30 seconds was most likely
    // intended to reduce the number of foreign key violation errors.
    // These should disappear with the introduction of the dependency tree,
    // as should the need to deviate from the default the retry interval.
    const interval = setInterval(
      () =>
        synchronize(family).catch(error => {
          logError('Error during family synchronization', error, crumbleFamily(family));
        }),
      LiveInterval.SECONDS_60,
    );
    reSyncTimeouts.push(interval);

    return state;
  }, new Set<ReplicationState<unknown>>());

  return {
    families,
    replicationStates: Array.from(replicationStates),
    reSyncTimeouts,
  };
}

export function crumble(replication: Replication): Crumbs {
  return {
    families: replication.families.length,
    reSyncTimeouts: replication.reSyncTimeouts.length,
    replicationStates: replication.replicationStates.length,
  };
}
